<template>
  <div>
    <div
      ref="mapExtendedBackground"
      class="
        @600/layout/block
        layout/absolute
        layout/bottom:0%
        layout/left:0%
        layout/none
        layout/right:0%
        layout/top:0%
        layout/z-index:1
      "
      style="
        background: linear-gradient(to left, #f6f6f6 50%, white 50%);
      "
    />
    <div
      id="visit-guide-map-page-vue"
      ref="visitGuideMapPageVue"
      class="
        @600/layout/grid:3
        @768/layout/grid:gap:clear
        layout/grid:1
        layout/relative
        layout/width:max
        layout/wrapper:rights-maximum
        layout/z-index:1
      "
      style="--layout--width--max: var(--page-width--maximum)"
      >
      <VisitGuideMapSection
        ref="mapAndToggle"
        :current-floor="currentFloor"
        :set-current-floor="setCurrentFloor"
        :highlighted-element-id="highlightedElementId"
        :active-art-rooms-by-hl-ids="roomsByHighlightID"
        :active-art-rooms-ready="activeArtRoomsReady"
        @elementClicked="handleElementClicked"
      />
      <VisitGuideExhibitionSection
        :current-floor="currentFloor"
        :floors="floors"
        :set-highlighted-element="setHighlightedElement"
        :show-drawer="showDrawer"
      />
      <Transition :name="transitionName">
        <OverlayDrawer
          v-if="drawerIsVisible"
          ref="overlay"
          :hide-drawer="hideDrawer"
          :overlay-data="overlayData"
          :is-single-column-layout="isSingleColumnLayout"
        />
      </Transition>
    </div>
  </div>
</template>

<script>
  import VisitGuideMapSection from './visit-guide-map.vue';
  import VisitGuideExhibitionSection from './visit-guide-map-exhibitions.vue';
  import { formatToSVGId, processExhibitionsData, getExhibitionOverlayData, getGalleryOverlayData } from 'Helpers/dvg-helpers/map-page-helpers.js';
  import OverlayDrawer from './overlay-drawer.vue';

  export default {
    components: {
      VisitGuideMapSection,
      VisitGuideExhibitionSection,
      OverlayDrawer
    },
    props: 
      {
        themeGroups: {
          type: Array,
          required: true
        }
      }
    ,
    data() {
      return {
        currentFloor: '1',
        highlightedElementId: null,
        drawerIsVisible: false,
        overlayData: null,
        transitionName: '',
        isSingleColumnLayout: '',
        floors: {},
        isLoaded: false,
        floorChanged: false,
        activeArtRoomsReady: false,
        roomsByHighlightID: [],
      };
    },
    watch: {
      isLoaded() {
        this.handleIsLoaded();
      },
      currentFloor() {
        this.roomsByHighlightID = this.getRoomsByHighlightID();
        this.activeArtRoomsReady = true;
      }
    },
    mounted() {
      this.getExhibitions();
      this.orientationChangeQuery = window.matchMedia('(orientation: portrait)');
      this.orientationChangeQuery.addListener(this.handleOrientationChange);
      this.setPageLayout();
      this.setTransitionName();
    },
    methods: {
      handleIsLoaded() {
        const {floor, exhibit} = this.getExhibitAndFloorFromParams();
        if (floor && this.isValidFloor(floor)) {
          this.setCurrentFloor(floor);
        }
        if (exhibit) {
          const highlightID = exhibit.highlightID;
          setTimeout(() => {
            this.handleElementClicked(highlightID);
          }, 1000);
        }
      },
      getRoomsByHighlightID() {
        const floorData = this.floors[this.currentFloor] || {};
        const exhibitions = floorData.exhibitions || [];
        const themes = floorData.themeGroup?.themes || [];

        const exhibitionHighlightIDs = exhibitions.map(exhibition => exhibition.highlightID);
        const themeHighlightIDs = themes.map(theme => theme.highlightID);

        return [...exhibitionHighlightIDs, ...themeHighlightIDs];
      },
      async getExhibitions() {
        const fetchURL = `/screens/directory.json?includeMdesHiddenOnScreens=true&imageSize=medium`;

        try {
          const res = await fetch(fetchURL);
          const data = await res.json();
          this.floors = processExhibitionsData(data, this.themeGroups);
          this.isLoaded = true;
          this.roomsByHighlightID = this.getRoomsByHighlightID();
          this.activeArtRoomsReady = true;
        } catch (error) {
          console.error('Error fetching exhibitions:', error);
        }
      },
      setCurrentFloor(floor) {
        const pageBannerHeight = document.getElementById('header-column-page-banner')?.clientHeight || 0;
        const scrollOffset = window.scrollY >= pageBannerHeight ? pageBannerHeight : 0;
        window.scrollTo(0, scrollOffset);
        this.currentFloor = floor;
        const scrollingContainer = document.querySelectorAll('.scrolling-container');
        Array.from(scrollingContainer).forEach((container) => {
          container.scrollLeft = 0;
        });
        this.hideDrawer();
      },
      setHighlightedElement(locationTitle) {
        const id = formatToSVGId(this.currentFloor, locationTitle);
        this.highlightedElementId = id;
        this.$refs.mapAndToggle.allowAutoZoomOut = true;
      },
      showDrawer(overlayData) {
        this.setTransitionName();
        document.body.style.overflow = 'hidden';
        this.overlayData = overlayData;
        this.drawerIsVisible = true;
      },
      hideDrawer() {
        this.setTransitionName();
        this.drawerIsVisible = false;
        this.highlightedElementId = null;
        document.body.style.overflow = 'auto';
      },
      handleOrientationChange() {
        this.setPageLayout();
        this.setTransitionName();
        this.$refs.overlay?.$el.style.setProperty('display', 'none');
        this.hideDrawer();
      },
      setTransitionName() {
        this.transitionName = this.isSingleColumnLayout ? 'slide-up' : 'slide-in';
      },
      setPageLayout() {
        this.isSingleColumnLayout = window.innerWidth < 600;
      },
      handleElementClicked(elementHighlightID) {
        const overlayData = this.getElementOverlayData(elementHighlightID);

        if (overlayData) {
          this.highlightedElementId = elementHighlightID;
          this.showDrawer(overlayData);
        } else {
          this.highlightedElementId = null;
          this.hideDrawer();
        }
      },
      /**
       * Searches the floor data for an exhibition or gallery with a highlightID that matches the given elementID.
       * If found, it returns the data formatted for display on the overlay. Otherwise it returns undefined.
       * @param {string} elementHighlightID - the highlightID of the element to search for
       * @returns {(Object|undefined)} the data to display on the overlay or undefined if no matching element is found
       */
      getElementOverlayData(elementHighlightID) {
        const floorData = this.floors[this.currentFloor];

        const exhibition = floorData.exhibitions?.find(exhibition => exhibition.highlightID === elementHighlightID);
        if (exhibition) {
          return getExhibitionOverlayData(exhibition);
        }

        const gallery = floorData.themeGroup?.themes.find(gallery => gallery.highlightID === elementHighlightID);
        if (gallery) {
          return getGalleryOverlayData(gallery);
        }
      },
      getExhibitAndFloorFromParams() {
        const params = new URLSearchParams(window.location.search);
        const locationParam = params.get('location');

        if (!locationParam) return {floor: null, exhibit: null};
        const locationIsEntireFloor = locationParam.toLowerCase().includes('floor');
        if (locationIsEntireFloor) {
          let floor = locationParam.split(' ')[1]; // convert non breaking space to regular space in order to split on space
          floor = this.mapFloorNumber(floor);
          return {floor, exhibit: null};
        }
        return this.findExhibitByLocation(locationParam);
      },
      isValidFloor(floor){
        return Object.keys(this.floors).includes(floor);
      },
      findExhibitByLocation(location) {
          let exhibition;
          let gallery;
          for (let floor in this.floors) {
            exhibition = this.floors[floor].exhibitions.find(exhibition => exhibition.LocationName == location);
            if (exhibition) return { floor, exhibit: exhibition};
            gallery = this.floors[floor].themeGroup?.themes.find(gallery => gallery.title == location);
            if (gallery) return {floor, exhibit: gallery};
          }
          return {floor: null, exhibit: null};
      },
      /**
       * Maps floor to a valid floor name in order to match with floor toggle options
       * @param {*} floor
       */
      mapFloorNumber(floor) {
        const floorMap = {
          'T2/T1': 'LL',
          'S': 'LL',
          '6W': '6',
          '3W': '3'
        };
        return floorMap[floor] || floor;
      }
    },
  };
</script>

<style>
  .slide-up-enter-active, .slide-up-leave-active {
    transition: top 0.5s;
  }
  .slide-up-enter,
  .slide-up-leave-to {
    top: 100%;
  }
  .slide-in-enter-active, .slide-in-leave-active {
    transition: transform 0.5s;
  }
  .slide-in-enter,
  .slide-in-leave-to {
    transform: translateX(-100%);
  }
</style>